import { useCallback, useEffect, useRef, useState } from "react";
import { Download, Paperclip, Plus, Trash2, Upload } from "lucide-react";

import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import {
  listOrganizationDocuments,
  uploadOrganizationDocument,
  deleteOrganizationDocument,
  getOrganizationDocumentBlob,
} from "./organizationDocumentApi.js";
import { formatSizeBytes, formatInstant, describeMouError } from "../mou/mouCaseShared.js";
import "./organizationDocuments.css";

const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";
const HINT = "รองรับไฟล์ PDF, JPG, PNG หรือ WEBP ขนาดไม่เกิน 20 MB";

/**
 * P164-R4: a company's OWN documents, inside its detail drawer.
 *
 * Owner: "บริษัทจัดหางานและ จัดหาแรงงานต้องแนบเอกสารได้ ผมยังไม่เห็นนะครับ".
 * They were right - P164 attached documents to an MOU ROUND, and only a job-side
 * company has rounds. A บริษัทจัดหาแรงงาน had nowhere at all to keep its licence,
 * and neither side had anywhere for the papers that belong to the COMPANY rather
 * than to one import.
 *
 * So this renders for BOTH sides, with no side check anywhere in it. That
 * absence is the feature.
 *
 * It stays in the drawer rather than moving to a dialog, unlike the MOU rounds:
 * a list of files with a type and a date is exactly what a 440px column is good
 * at, and there is no eight-step process to run inside it. The type is picked
 * from a datalist rather than a select, because the papers a company holds vary
 * and a closed list would refuse the one it does not know about.
 */
export default function OrganizationDocumentsSection({ organizationId }) {
  const [state, setState] = useState({ status: "idle", data: null, error: null });
  const [documentType, setDocumentType] = useState("");
  const [pending, setPending] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteState, setDeleteState] = useState({ status: "idle", error: null });
  const fileInputRef = useRef(null);

  /**
   * A failure to READ the list needs its own wording. The shared mapper turns
   * every 404 into the "it may have been deleted" line, which is right for one
   * document that is gone and badly wrong here: a 404 on the LIST means this
   * server has no such endpoint at all, so nothing was deleted and the operator
   * is being sent looking for something that never existed. Say what is true.
   */
  function describeListFailure(error) {
    if (error?.status === 404) {
      return "เซิร์ฟเวอร์นี้ยังไม่รองรับการแนบเอกสารของบริษัท (ระบบหลังบ้านยังไม่ได้อัปเดต) — ไม่มีเอกสารใดหายไป";
    }
    return describeMouError(error, "ไม่สามารถอ่านเอกสารของบริษัทนี้ได้ในขณะนี้");
  }

  const load = useCallback(() => {
    if (!organizationId) {
      return undefined;
    }
    let cancelled = false;
    setState((previous) => ({ status: "loading", data: previous.data, error: null }));
    listOrganizationDocuments(organizationId)
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", data, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          // Keep whatever was already loaded: a failed refresh must not replace
          // a usable list with an error panel.
          setState((previous) => ({ status: "error", data: previous.data, error }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  useEffect(() => load(), [load]);

  const run = useCallback(async (what, action) => {
    setPending(what);
    setActionError(null);
    try {
      const refreshed = await action();
      setState({ status: "ready", data: refreshed, error: null });
    } catch (caught) {
      const named = caught?.wxFileName ? caught.wxFileName + ": " : "";
      setActionError(named + describeMouError(caught, "ไม่สามารถทำรายการกับเอกสารได้ กรุณาลองใหม่อีกครั้ง"));
    } finally {
      setPending(null);
    }
  }, []);

  /**
   * Attaches every file the operator picked. Owner: "ต้องแนบได้หลายเอกสาร".
   *
   * Sequential, never Promise.all: each upload answers with the refreshed LIST,
   * so firing them together would race three responses that each believe they
   * are the final state, and the panel would settle on whichever landed last.
   * Run in order, the last response genuinely is the last state.
   *
   * A failure stops the run and names the file that failed, rather than pressing
   * on and leaving the operator to work out which of five went missing. Whatever
   * uploaded before it is already on the server, so that list is still shown.
   */
  function pickFiles(files) {
    const label = documentType.trim();
    run("upload", async () => {
      let refreshed = null;
      for (const file of files) {
        try {
          refreshed = await uploadOrganizationDocument(organizationId, file, { documentType: label || null });
        } catch (caught) {
          if (refreshed) {
            setState({ status: "ready", data: refreshed, error: null });
          }
          throw Object.assign(caught, { wxFileName: file.name });
        }
      }
      setDocumentType("");
      return refreshed;
    });
  }

  function confirmDelete() {
    if (!pendingDelete) {
      return;
    }
    setDeleteState({ status: "deleting", error: null });
    deleteOrganizationDocument(organizationId, pendingDelete.documentId)
      .then((refreshed) => {
        setState({ status: "ready", data: refreshed, error: null });
        setPendingDelete(null);
        setDeleteState({ status: "idle", error: null });
      })
      .catch((error) => {
        setDeleteState({ status: "idle", error: describeMouError(error, "ลบเอกสารไม่สำเร็จ กรุณาลองใหม่อีกครั้ง") });
      });
  }

  /**
   * Reads one document's real bytes. The file route is authenticated, so they
   * are fetched with the session token and handed to the browser as an object
   * URL - a plain href would carry no token and 401.
   *
   * `mode` decides where they go: "open" views the file in a new tab,
   * "download" saves it under its own name. Owner asked for the second
   * ("download ได้"), and both earn their place - somebody checking which
   * licence this is wants to look at it, somebody sending it to an office wants
   * the file. One fetch path, so the two can never quietly diverge.
   */
  async function readDocument(item, mode) {
    setActionError(null);
    try {
      const { blob } = await getOrganizationDocumentBlob(organizationId, item.documentId);
      if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
        setActionError("เบราว์เซอร์นี้เปิดไฟล์ที่แนบไม่ได้");
        return;
      }
      const objectUrl = URL.createObjectURL(blob);

      if (mode === "download") {
        const link = window.document.createElement("a");
        link.href = objectUrl;
        // The stored file name, so the file lands named what it is - a direct
        // link would only ever reveal the random storage key.
        link.download = item.fileName || item.documentType || "document";
        window.document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        window.open(objectUrl, "_blank", "noopener");
      }

      // Revoked on a delay: the new tab (or the save) has to read the URL
      // first, and revoking in the same tick leaves it blank.
      setTimeout(() => URL.revokeObjectURL?.(objectUrl), 60_000);
    } catch (caught) {
      setActionError(describeMouError(caught, "เปิดไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง"));
    }
  }

  const list = state.data;
  const documents = list?.items ?? [];
  const canWrite = list?.canWrite ?? false;
  const busy = pending !== null;

  return (
    <>
      <section className="wx-wl-dsec wx-orgdoc-section">
        <h3>เอกสารของบริษัท ({list?.totalCount ?? 0})</h3>

        {state.status === "loading" && state.data === null && <p className="wx-empty-list">กำลังโหลดเอกสาร...</p>}

        {state.status === "error" && (
          <p className="wx-wl-inline-error" role="alert">
            {describeListFailure(state.error)}
          </p>
        )}

        {list && documents.length === 0 && <p className="wx-empty-list">ยังไม่มีเอกสารของบริษัทนี้</p>}

        {documents.length > 0 && (
          <ul className="wx-orgdoc-list">
            {documents.map((document) => {
              const size = formatSizeBytes(document.sizeBytes);
              const uploadedAt = formatInstant(document.uploadedAtUtc);
              const name = document.fileName || document.documentType || "เอกสารแนบ";
              return (
                <li className="wx-orgdoc-row" key={document.documentId}>
                  <span className="wx-orgdoc-icon" aria-hidden="true">
                    <Paperclip size={14} />
                  </span>
                  <span className="wx-orgdoc-text">
                    <strong>{document.documentType || name}</strong>
                    <button type="button" className="wx-orgdoc-open" onClick={() => readDocument(document, "open")}>
                      {name}
                    </button>
                    <span className="wx-orgdoc-meta">{[size, uploadedAt].filter(Boolean).join(" · ")}</span>
                  </span>
                  {/* Downloading is a READ, so it is offered to anyone who can
                      see the document - a read-only viewer still has to be able
                      to send the company's licence to an office. */}
                  <button
                    type="button"
                    className="wx-orgdoc-action"
                    aria-label={`ดาวน์โหลด ${document.documentType || name}`}
                    title="ดาวน์โหลดไฟล์นี้"
                    onClick={() => readDocument(document, "download")}
                  >
                    <Download size={14} aria-hidden="true" />
                  </button>

                  {canWrite && (
                    <button
                      type="button"
                      className="wx-orgdoc-action wx-orgdoc-action--danger"
                      aria-label={`ลบเอกสาร ${document.documentType || name}`}
                      disabled={busy}
                      onClick={() => {
                        setDeleteState({ status: "idle", error: null });
                        setPendingDelete(document);
                      }}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {canWrite && (
          <div className="wx-orgdoc-add">
            <p className="wx-orgdoc-hint">
              {HINT} — เลือกได้หลายไฟล์พร้อมกัน ใส่ชื่อประเภทเอกสารก่อนเลือกไฟล์ เพื่อให้คนอื่นรู้ว่าไฟล์นี้คืออะไร
            </p>
            <div className="wx-orgdoc-add-row">
              <input
                type="text"
                aria-label="ประเภทเอกสาร"
                placeholder="เช่น ใบอนุญาตจัดหางาน"
                list="wx-orgdoc-types"
                maxLength={120}
                value={documentType}
                disabled={busy}
                onChange={(event) => setDocumentType(event.target.value)}
              />
              {/* A datalist, never a select: these are the papers a company
                  usually files, but the one it actually has in hand may not be
                  on any list somebody wrote in advance. */}
              <datalist id="wx-orgdoc-types">
                {(list?.suggestedDocumentTypes ?? []).map((type) => (
                  <option key={type} value={type} />
                ))}
              </datalist>
              <button
                type="button"
                className="wx-button wx-button--ghost"
                disabled={busy}
                onClick={() => fileInputRef.current?.click()}
              >
                {pending === "upload" ? <Upload size={14} aria-hidden="true" /> : <Plus size={14} aria-hidden="true" />}
                {pending === "upload" ? "กำลังอัปโหลด..." : "แนบเอกสาร"}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                className="wx-visually-hidden"
                aria-label="แนบเอกสารของบริษัท"
                accept={ACCEPT}
                // Owner: "ต้องแนบได้หลายเอกสาร". A company files its licence,
                // its affidavit and its ภ.พ.20 in one sitting; making that three
                // separate trips through a file picker was the wrong shape.
                multiple
                disabled={busy}
                onChange={(event) => {
                  const files = Array.from(event.target.files ?? []);
                  // Cleared so picking the same file twice still fires a change.
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                  if (files.length > 0) {
                    pickFiles(files);
                  }
                }}
              />
            </div>
          </div>
        )}

        {actionError && (
          <p className="wx-wl-form-message" role="alert">
            {actionError}
          </p>
        )}
      </section>

      {/* A sibling of the section, not a child: .wx-wl-dsec restyles every
          descendant h3 to an 11.5px uppercase label, which would silently
          rewrite the confirmation's own title. */}
      <ConfirmDialog
        open={pendingDelete !== null}
        title="ลบเอกสารนี้?"
        body="เอกสารนี้จะหายไปจากรายการของบริษัท การลบนี้ย้อนกลับจากหน้าจอไม่ได้"
        detail={
          pendingDelete && (
            <>
              <strong>{pendingDelete.documentType || pendingDelete.fileName || "เอกสารแนบ"}</strong>
              <span>
                {[pendingDelete.fileName, formatSizeBytes(pendingDelete.sizeBytes), formatInstant(pendingDelete.uploadedAtUtc)]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </>
          )
        }
        confirmLabel="ลบเอกสาร"
        cancelLabel="ไม่ลบ"
        tone="danger"
        busy={deleteState.status === "deleting"}
        error={deleteState.error}
        onConfirm={confirmDelete}
        onCancel={() => {
          setPendingDelete(null);
          setDeleteState({ status: "idle", error: null });
        }}
      />
    </>
  );
}
